import { Button, Stack, Typography } from '@mui/material'
import { useCallback, useEffect } from 'react'
import { useFieldArray } from 'react-hook-form'
import { TextInputField } from 'utoniq-ui'

import {
  MembershipInterval,
  RegisterSubscriptionsInput,
} from '@/clients/utoniq-core/schema'
import {
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

import { PriceInputCell } from './components'

export type MembershipCreateFormProps = {
  startMembershipNumber: number | null
  onSubmit: (values: CreateMembershipProps) => Promise<void>
}

export type CreateMembershipProps = RegisterSubscriptionsInput

const schema: UseFormProps['schema'] = o => ({
  title: o
    .string()
    .max(50)
    .allow(null)
    .optional()
    .label(tLabel('consoleMembershipCreate.title')),
  content: o
    .string()
    .max(2000)
    .label(tLabel('consoleMembershipCreate.content')),
  annualContent: o
    .string()
    .allow(null, '')
    .optional()
    .max(2000)
    .label(tLabel('consoleMembershipCreate.annualContent')),
  plans: o.array().items({
    amount: o.number(),
    interval: o.string(),
    membershipNumberPrefix: o
      .string()
      .max(5)
      .optional()
      .allow(null, '')
      .pattern(/^[A-Za-z]*$/)
      .label(tLabel('consoleMembershipCreate.membershipNumberPrefix')),
  }),
  startMembershipNumber: o
    .number()
    .integer()
    .min(1)
    .max(99999999)
    .optional()
    .allow(null)
    .label(tLabel('consoleMembershipCreate.startMembershipNumber')),
})

export const MembershipCreateForm: Organism<MembershipCreateFormProps> = ({
  onSubmit,
  startMembershipNumber,
}) => {
  const { t } = useTranslation('consoleMembershipCreate')
  const { showError } = useMessage()

  const {
    handleSubmit,
    register,
    setValue,
    setError,
    watch,
    control,
    formState: { isSubmitting, isValid, errors },
  } = useForm<CreateMembershipProps>({
    schema,
    defaultValues,
  })

  const planWatch = watch('plans')
  const hasYearlyPlan = planWatch?.length === 2

  const { fields: planFields } = useFieldArray({ control, name: 'plans' })

  useEffect(() => {
    setValue('startMembershipNumber', startMembershipNumber)
  }, [startMembershipNumber])

  const handleOnSubmit = useCallback(
    async (values: CreateMembershipProps) => {
      await onSubmit(values).catch(error => {
        handleError<CreateMembershipProps>(error, setError, showError)
      })
    },
    [onSubmit, setError, showError]
  )

  return (
    <OrganismFrame sx={{ px: 5 }}>
      <TextInputField
        type="input"
        t={t}
        textFieldProps={{ sx: { mb: 0 } }}
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        {...register('title')}
        error={errors?.title}
      />
      <PriceInputCell
        watch={watch}
        setValue={setValue}
        hasYearlyPlan={hasYearlyPlan}
        control={control}
        register={register}
      />
      <TextInputField
        type="textarea"
        t={t}
        textFieldProps={{
          multiline: true,
          rows: 13,
          sx: { mb: 0 },
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        {...register('content')}
        error={errors?.content}
      />
      {hasYearlyPlan && (
        <TextInputField
          type="textarea"
          t={t}
          textFieldProps={{
            multiline: true,
            rows: 13,
            sx: { mb: 0 },
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          titleProps={{ variant: 'h5', color: 'text.primary' }}
          {...register('annualContent')}
          error={errors?.annualContent}
        />
      )}
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="h3">{t('membershipNumberTitle')}</Typography>
      </Stack>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('membershipNumberDesc')}
        </Typography>
      </Stack>

      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="h5">{t('membershipNumberPrefixTitle')}</Typography>
      </Stack>
      <Stack direction="row" sx={{ mt: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('membershipNumberPrefixDesc')}
        </Typography>
      </Stack>

      {planFields.map((field, index) => (
        <TextInputField
          key={field.id}
          type="input"
          t={t}
          textFieldProps={{
            sx: { mb: 0 },
            placeholder: t('membershipNumberPrefixPlaceholder'),
          }}
          titleProps={{
            variant: 'h5',
            color: 'text.primary',
            children: t(`${field.interval}lyMembershipNumberPrefix`),
          }}
          {...register(`plans.${index}.membershipNumberPrefix`)}
          error={errors?.plans?.[index]?.membershipNumberPrefix}
        />
      ))}
      <TextInputField
        type="input"
        t={t}
        disabled={!!startMembershipNumber}
        textFieldProps={{ sx: { mb: 0 } }}
        titleProps={{
          variant: 'h5',
          color: 'text.primary',
          children: (
            <>
              <Stack direction="row" sx={{ mt: 2 }}>
                <Typography variant="h5">
                  {t('startMembershipNumberTitle')}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('startMembershipNumberDesc')}
                </Typography>
              </Stack>
            </>
          ),
        }}
        {...register('startMembershipNumber')}
        error={errors?.startMembershipNumber}
      />
      <Typography variant="subtitle1" sx={{ mt: 7 }}>
        {t('aboutSales')}
      </Typography>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="overline">・</Typography>
        <Typography variant="caption">{t('aboutSalesDetail1')}</Typography>
      </Stack>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="overline">・</Typography>
        <Typography variant="caption">{t('aboutSalesDetail2')}</Typography>
      </Stack>
      <Stack sx={{ mt: 6, mb: 4 }} justifyContent="center" alignItems="center">
        <Button
          sx={{ width: 244, py: 3 }}
          onClick={handleSubmit(handleOnSubmit)}
          loading={isSubmitting}
          disabled={!isValid || isSubmitting}
        >
          {t('startMembership')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}

const defaultValues: CreateMembershipProps = {
  title: '',
  content: '',
  annualContent: null,
  plans: [
    {
      amount: 480,
      interval: MembershipInterval.Month,
      membershipNumberPrefix: null,
    },
  ],
  startMembershipNumber: null,
}

export const monthlyFees = [
  300, 480, 500, 780, 800, 980, 1000, 1180, 1200, 5000, 8000, 10000,
].map(amount => ({ value: amount.toString(), label: amount.toString() }))
